import { createContext, useContext } from "react";
import { type AgentStatus } from "@/features/chat/types/sdk";
import { useStreamWithBackend } from "@/features/chat/hooks/useStreamWithBackend";
import { type AddressData } from "@/services/conversation-service";
import { type LoaderStatus } from "@/providers/StreamState/useFirstTokenDetection";

// Use production-ready streaming hook
type UseStreamReturn = ReturnType<typeof useStreamWithBackend>;
type BaseStreamContextType = UseStreamReturn;

/**
 * StreamContext type definition.
 * Extends the base stream context with additional state for:
 * - firstTokenReceived: Loader visibility state
 * - currentStatus: Current loader animation status
 * - resetFirstTokenReceived: Reset function for loader state
 * - threadId: Current thread ID with sessionStorage persistence
 * - setThreadId: Setter for thread ID
 * - sendToolApproval: Handler for human-in-the-loop tool approval
 * - createProject: Create a new project via backend API
 * - agentStatus: Current agent execution status from Firestore
 * - agentStatusLoading: Loading state for agent status
 * - agentStatusError: Error from agent status subscription
 * - isRealtimeListening: Whether real-time message listener is active
 * - hasActiveStream: Whether SSE stream is currently active (true during streaming)
 */
export type StreamContextType = BaseStreamContextType & {
  firstTokenReceived: boolean | null;
  currentStatus: LoaderStatus;
  resetFirstTokenReceived: () => void;
  threadId: string | null;
  setThreadId: (id: string | null) => void;
  sendToolApproval: (params: {
    toolUseId: string;
    toolName: string;
    action: "approve" | "edit" | "reject";
    editedInput?: Record<string, any>;
    rejectionMessage?: string;
  }) => Promise<void>;
  createProject: (conversationName: string, projectId?: string, address?: AddressData | null) => Promise<string>;
  // Agent process visibility fields
  agentStatus: AgentStatus | null;
  agentStatusLoading: boolean;
  agentStatusError: string | null;
  isRealtimeListening: boolean;
  hasActiveStream: boolean;
};

/**
 * React context for stream state.
 * Single responsibility: Provide stream context to all child components.
 */
export const StreamContext = createContext<StreamContextType | undefined>(
  undefined,
);

/**
 * Custom hook to use the stream context.
 * Single responsibility: Safely access stream context with error handling.
 *
 * @throws Error if used outside StreamProvider
 * @returns StreamContext value
 */
export function useStreamContext(): StreamContextType {
  const context = useContext(StreamContext);
  if (context === undefined) {
    throw new Error("useStreamContext must be used within a StreamProvider");
  }
  return context;
}
